import { useEffect, useState } from "react";

/**
 * Tailwind's breakpoint scale, as a hook.
 *
 * Layout decisions that CSS cannot express (which panel mode a shell should default to, whether a
 * panel becomes an overlay drawer) need the breakpoint in JS. Reading it from one place keeps those
 * decisions consistent with the `sm:`/`md:`/`lg:` classes used for everything else, instead of each
 * module inventing its own pixel thresholds.
 */
export const BREAKPOINTS = {
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280,
	"2xl": 1536,
} as const;

export type Breakpoint = "xs" | keyof typeof BREAKPOINTS;

/** Widest-first so the first match wins. */
const ORDERED: Array<[Breakpoint, number]> = [
	["2xl", BREAKPOINTS["2xl"]],
	["xl", BREAKPOINTS.xl],
	["lg", BREAKPOINTS.lg],
	["md", BREAKPOINTS.md],
	["sm", BREAKPOINTS.sm],
];

function breakpointForWidth(width: number): Breakpoint {
	for (const [name, min] of ORDERED) {
		if (width >= min) return name;
	}
	return "xs";
}

const RANK: Record<Breakpoint, number> = { xs: 0, sm: 1, md: 2, lg: 3, xl: 4, "2xl": 5 };

/** True when the current breakpoint is at least `min` — the JS equivalent of a `min-w` media query. */
export function atLeast(current: Breakpoint, min: Breakpoint): boolean {
	return RANK[current] >= RANK[min];
}

/**
 * The current breakpoint, updated on resize. SSR-safe: assumes the widest layout before hydration so
 * the desktop arrangement is not briefly rendered as a mobile one.
 */
export function useBreakpoint(): Breakpoint {
	const [bp, setBp] = useState<Breakpoint>(() =>
		typeof window === "undefined" ? "2xl" : breakpointForWidth(window.innerWidth),
	);

	useEffect(() => {
		const onResize = () => {
			setBp((prev) => {
				const next = breakpointForWidth(window.innerWidth);
				return next === prev ? prev : next;
			});
		};
		window.addEventListener("resize", onResize);
		onResize();
		return () => window.removeEventListener("resize", onResize);
	}, []);

	return bp;
}
